import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import {
	RequestActionResponse,
	RequestItem,
	RequestStatus,
	RequestsService,
} from '@services/requests/requests.service';
import { MenuService, PageTabs } from '@services/shared/menu.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { TableBase } from '@shared/bases/table.base';
import { TableComponent } from '@shared/components/table/table.component';
import { TableAction, TableColumn } from '@shared/components/table/table.model';
import { TabHeader, TabsComponent } from '@shared/components/tabs/tabs.component';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale, formatEur } from 'app/core/utils/locale';
import { catchError, of } from 'rxjs';

interface RequestTableData extends RequestItem {
	orderSummary: string;
	userName: string;
	userEmail: string;
	productName: string;
	platformName: string;
	paymentMethodLabel: string;
	priceFormatted: string;
}

@Component({
	selector: 'app-requests',
	imports: [SharedModule, TableComponent, TabsComponent],
	templateUrl: './requests.component.html',
	styleUrl: './requests.component.scss',
})
export class RequestsComponent extends TableBase implements OnInit, OnDestroy {
	private menuService = inject(MenuService);

	data: RequestTableData[] = [];
	activeStatus: RequestStatus = 'PENDING_VERIFICATION';
	selectedTab = 0;

	tabs: TabHeader[] = [];

	headers: TableColumn[] = [];

	override mainField = 'orderSummary';
	override sortField = 'createdAt';
	override sortOrder = -1;
	override limit = 10;
	loadError = false;

	constructor(
		private requestsService: RequestsService,
		private messageAlertService: MessageAlertService,
		private confirmDialogService: ConfirmDialogService,
		private translocoService: TranslocoService,
		private cdr: ChangeDetectorRef,
		override viewportService: ViewportService,
	) {
		super(viewportService);

		this.tabs = [
			{ label: this.translocoService.translate('requestsPage.tabs.pendingVerification'), icon: 'Clock' },
			{ label: this.translocoService.translate('requestsPage.tabs.pendingPayment'), icon: 'CreditCard' },
		];

		this.headers = [
			{ field: 'orderSummary', header: this.translocoService.translate('requestsPage.headers.order') },
			{ field: 'userName', header: this.translocoService.translate('requestsPage.headers.user') },
			{ field: 'url', header: this.translocoService.translate('requestsPage.headers.url') },
			{ field: 'priceFormatted', header: this.translocoService.translate('requestsPage.headers.price') },
			{ field: 'paymentMethodLabel', header: this.translocoService.translate('requestsPage.headers.method') },
		];
	}

	override loadData(): void {
		this.loadRequests();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;
		this.publishPageTabs();
		this.loadRequests();
	}

	// ====== Page tabs (topbar on desktop) ======

	private pageTabs: PageTabs | null = null;

	private publishPageTabs(): void {
		if (this.tabs.length === 0) return;
		this.pageTabs = {
			tabs: this.tabs,
			selected: this.selectedTab,
			onChange: (index) => this.onTabChange(index),
		};
		this.menuService.setPageTabs(this.pageTabs);
	}

	ngOnDestroy(): void {
		if (this.pageTabs) this.menuService.clearPageTabs(this.pageTabs);
	}

	onTabChange(tabIndex: number): void {
		this.selectedTab = tabIndex;
		if (this.pageTabs) this.pageTabs.selected = tabIndex;
		this.activeStatus = tabIndex === 0 ? 'PENDING_VERIFICATION' : 'PENDING_PAYMENT';
		this.page = 1;
		this.loadRequests();
	}

	loadRequests(): void {
		this.isLoading = true;
		this.loadError = false;

		const params = {
			page: this.page,
			limit: this.limit,
			sort_order: this.sortOrder === 1 ? 'asc' : ('desc' as 'asc' | 'desc'),
			status: this.activeStatus,
		};

		this.requestsService
			.getRequests(params)
			.pipe(
				catchError((err) => {
					console.error('Error loading requests:', err);
					this.data = [];
					this.totalRecords = 0;
					this.isLoading = false;
					this.loadError = true;
					this.cdr.detectChanges();
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (response?.data) {
					this.data = response.data.map((request) => ({
						...request,
						orderSummary: `${request.amount} ${request.product?.name || this.translocoService.translate('requestsPage.fallback.product')} (${request.platform?.name || '-'})`,
						userName: request.user
							? `${request.user.name || ''} ${request.user.lastName || ''}`.trim() || request.user.email
							: '-',
						userEmail: request.user?.email || '-',
						productName: request.product?.name || '-',
						platformName: request.platform?.name || '-',
						priceFormatted: formatEur(request.price ?? 0, activeLocale(this.translocoService)),
						paymentMethodLabel: this.getPaymentMethodLabel(request.paymentMethod),
					}));
					this.totalRecords = response.total;
				}
				this.isLoading = false;
				this.cdr.detectChanges();
			});
	}

	private getPaymentMethodLabel(method: string | null): string {
		switch (method) {
			case 'REVOLUT':
				return 'Revolut';
			case 'BIZUM':
				return 'Bizum';
			default:
				return method || '-';
		}
	}

	getMenuActions(request: RequestTableData): TableAction[] {
		return [
			{
				id: 1,
				label: this.translocoService.translate('requestsPage.actions.approve'),
				icon: 'Check',
				action: 'approve',
				class: 'green',
				command: () => this.onApprove(request),
			},
			{
				id: 2,
				label: this.translocoService.translate('requestsPage.actions.reject'),
				icon: 'X',
				action: 'reject',
				class: 'red',
				separator: true,
				command: () => this.onReject(request),
			},
		];
	}

	menuActionTriggered(event: { action: string; data: RequestTableData | RequestTableData[] }): void {
		// approve/reject act on one request (the row's menu); bulk selections have no such action
		if (Array.isArray(event.data)) return;
		switch (event.action) {
			case 'approve':
				this.onApprove(event.data);
				break;
			case 'reject':
				this.onReject(event.data);
				break;
		}
	}

	onApprove(request: RequestTableData): void {
		const userName = request.userName || request.userEmail;
		this.confirmDialogService.confirmAndExecute(
			'approve',
			this.translocoService.translate('requestsPage.messages.approveConfirm', { name: userName }),
			() => this.requestsService.approveRequest(request.id),
			{
				// One toast, with the server's outcome: sent, waiting for provider balance, queued or send error
				showSuccessToast: false,
				onSuccess: (response?: RequestActionResponse) => {
					this.messageAlertService.showMessage(
						response?.providerOrderId ? 'success' : 'warn',
						this.translocoService.translate('requestsPage.messages.approveSuccess'),
						response?.message ||
							this.translocoService.translate('requestsPage.messages.approveSuccessDetail'),
					);
					this.loadRequests();
				},
				errorMessage: this.translocoService.translate('requestsPage.messages.approveError'),
			},
		);
	}

	onReject(request: RequestTableData): void {
		const userName = request.userName || request.userEmail;
		this.confirmDialogService.confirmAndExecute(
			'reject',
			this.translocoService.translate('requestsPage.messages.rejectConfirm', { name: userName }),
			() => this.requestsService.rejectRequest(request.id),
			{
				showSuccessToast: false,
				onSuccess: () => {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('requestsPage.messages.rejectSuccess'),
						this.translocoService.translate('requestsPage.messages.rejectSuccessDetail'),
					);
					this.loadRequests();
				},
				errorMessage: this.translocoService.translate('requestsPage.messages.rejectError'),
			},
		);
	}
}
